// Paramètres → Modèles : modèles d'emails interpolés par l'add-in Outlook (admin).
import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil } from 'lucide-react';
import { Card } from '../ui/Card';
import {
  useEmailTemplates, useCreateEmailTemplate, useUpdateEmailTemplate,
} from '../../hooks/queries/useEmailTemplates';
import type { EmailTemplate } from '../../services/documents/emailTemplateService';

const CATEGORIES = [
  { value: 'email_type', label: 'Email type' },
  { value: 'fee_agreement', label: 'Convention d’honoraires' },
  { value: 'acte', label: 'Acte' },
] as const;

type EmailCategory = (typeof CATEGORIES)[number]['value'];

interface FormState {
  id: string | null;
  name: string;
  description: string;
  category: EmailCategory;
  body_html: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  id: null,
  name: '',
  description: '',
  category: 'email_type',
  body_html: '',
  is_active: true,
};

function categoryLabel(category: string): string {
  return CATEGORIES.find((c) => c.value === category)?.label ?? category;
}

function asCategory(value: string): EmailCategory {
  return CATEGORIES.some((c) => c.value === value) ? (value as EmailCategory) : 'email_type';
}

export function EmailTemplatesSection() {
  const { data: templates, isLoading } = useEmailTemplates(false);
  const createTemplate = useCreateEmailTemplate();
  const updateTemplate = useUpdateEmailTemplate();
  const [form, setForm] = useState<FormState | null>(null);

  const openEdit = (t: EmailTemplate) => setForm({
    id: t.id,
    name: t.name,
    description: t.description ?? '',
    category: asCategory(t.category),
    body_html: t.body_html,
    is_active: t.is_active,
  });

  const submit = () => {
    if (!form || !form.name.trim() || !form.body_html.trim()) {
      toast.error('Nom et corps HTML sont obligatoires.');
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      category: form.category,
      body_html: form.body_html,
      is_active: form.is_active,
    };
    const mutation = form.id
      ? updateTemplate.mutateAsync({ id: form.id, patch: payload })
      : createTemplate.mutateAsync(payload);
    mutation
      .then(() => { toast.success('Modèle enregistré.'); setForm(null); })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Échec de l’enregistrement.'));
  };

  return (
    <div className="max-w-6xl space-y-4">
      <div className="flex max-w-3xl items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neutral-800">Modèles d’emails</h2>
          <p className="text-sm text-neutral-500">
            Trames HTML insérées depuis l’add-in Outlook. Variables : {'{{dossier.number}}'}, {'{{dossier.title}}'}, {'{{partie.nom}}'}, {'{{avocat.nom_complet}}'}, {'{{cabinet.nom}}'}, {'{{convention.mode}}'}, {'{{date.aujourdhui}}'}.
          </p>
        </div>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded bg-cime-primary px-3 py-1.5 text-sm font-medium text-white cursor-pointer"
          onClick={() => setForm(EMPTY_FORM)}
        >
          <Plus className="w-4 h-4" aria-hidden /> Nouveau modèle
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Chargement…</p>
      ) : (
        <ul className="max-w-3xl space-y-2">
          {(templates ?? []).map((t) => (
            <li key={t.id}>
              <Card>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-800">
                      {t.name}
                      {!t.is_active && <span className="ml-2 text-xs text-neutral-400">(inactif)</span>}
                    </p>
                    <p className="truncate text-xs text-neutral-500">
                      {t.description || categoryLabel(t.category)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className="rounded border border-neutral-200 px-2 py-1 text-xs text-neutral-600 hover:border-neutral-300 cursor-pointer"
                      onClick={() =>
                        updateTemplate.mutate(
                          { id: t.id, patch: { is_active: !t.is_active } },
                          { onError: (e) => toast.error(e instanceof Error ? e.message : 'Échec.') },
                        )}
                    >
                      {t.is_active ? 'Désactiver' : 'Activer'}
                    </button>
                    <button
                      type="button"
                      className="flex items-center gap-1 rounded border border-neutral-200 px-2 py-1 text-xs text-neutral-600 hover:border-neutral-300 cursor-pointer"
                      onClick={() => openEdit(t)}
                    >
                      <Pencil className="w-3.5 h-3.5" aria-hidden /> Modifier
                    </button>
                  </div>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {form && (
        <Card header={<span className="text-sm font-semibold">{form.id ? 'Modifier le modèle' : 'Nouveau modèle'}</span>}>
          <div className="max-w-3xl space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="text-neutral-600">Nom *</span>
                <input className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="text-neutral-600">Catégorie</span>
                <select className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm"
                  value={form.category} onChange={(e) => setForm({ ...form, category: asCategory(e.target.value) })}>
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block text-sm">
              <span className="text-neutral-600">Description (affichée sur la carte de choix)</span>
              <input className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm"
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="text-neutral-600">Corps HTML *</span>
              <textarea rows={12} className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 font-mono text-xs"
                value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="rounded border-neutral-300"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              <span className="text-neutral-600">Actif</span>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 cursor-pointer"
                onClick={() => setForm(null)}>
                Annuler
              </button>
              <button type="button"
                className="rounded bg-cime-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 cursor-pointer"
                disabled={createTemplate.isPending || updateTemplate.isPending}
                onClick={submit}>
                Enregistrer
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
